import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

export const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' },
  { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'he', name: 'עברית', flag: '🇮🇱', dir: 'rtl' },
  { code: 'ur', name: 'اردو', flag: '🇵🇰', dir: 'rtl' }
];

function LanguageSwitcher({ className = '' }) {
  const { i18n } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const currentLanguage = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    // Set initial document direction based on current language
    const language = LANGUAGES.find(lang => lang.code === i18n.language);
    if (language) {
      document.documentElement.setAttribute('dir', language.dir || 'ltr');
      document.documentElement.setAttribute('lang', language.code);
    }
  }, [i18n.language]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (langCode) => {
    const language = LANGUAGES.find(lang => lang.code === langCode);
    i18n.changeLanguage(langCode);
    
    // Set document direction for RTL languages
    if (language && language.dir === 'rtl') {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', langCode);
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', langCode);
    }
    
    setShowDropdown(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        data-testid="language-switcher-button"
        className="flex items-center space-x-2 px-3 py-2 bg-[#F8F5F0] dark:bg-[#2A2520] hover:bg-[#E8E0D4] dark:hover:bg-gray-600 rounded-lg transition-colors border border-[#E0D8CC] dark:border-[#2A2520]"
        title="Change Language"
      >
        <Globe className="w-4 h-4 text-[#2A2420] dark:text-[#F0EBE4]" />
        <span className="text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] hidden sm:inline">
          {currentLanguage.flag} {currentLanguage.code.toUpperCase()}
        </span>
        <span className="text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] sm:hidden">
          {currentLanguage.flag}
        </span>
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#22201C] rounded-lg shadow-xl border border-[#E0D8CC] dark:border-[#2A2520]/50 py-2 z-50">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              data-testid={`language-option-${lang.code}`}
              className={`w-full px-4 py-2 text-left hover:bg-[#F8F5F0] dark:hover:bg-[#2A2520]/50 transition-colors flex items-center space-x-3 ${
                i18n.language === lang.code ? 'bg-[#F8F5F0] dark:bg-[#2A2520]' : ''
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4]">
                {lang.name}
              </span>
              {i18n.language === lang.code && (
                <span className="ml-auto text-[#8B9D83] dark:text-[#68D391]">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
